import React, { useEffect, useRef } from 'react';
import Hero from '../../components/ui/Hero';
import { Gamepad, ArrowLeft, ArrowRight, ArrowUp } from 'lucide-react';

const YetiEscapePage = () => {
  const gameContainerRef = useRef(null);
  // Create a game instance ref to access it from touch handlers
  const gameInstanceRef = useRef(null);
  const playerRef = useRef(null);
  
  useEffect(() => {
    // Load Phaser script dynamically
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/phaser/3.55.2/phaser.min.js';
    script.async = true;
    
    script.onload = () => {
      // Initialize game after Phaser loads
      initGame();
    };
    
    document.body.appendChild(script);
    
    // Cleanup function to remove the script when component unmounts
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
      // Destroy any existing game instance
      if (gameInstanceRef.current) {
        gameInstanceRef.current.destroy(true);
      }
    };
  }, []);
  
  const initGame = () => {
    // Debug flag - Set to true to show physics boundaries, false to hide them
    const SHOW_DEBUG_BOUNDARIES = false;
    
    // Game configuration
    const config = {
      type: Phaser.AUTO,
      width: 800,
      height: 500,
      parent: gameContainerRef.current,
      backgroundColor: '#4b6584', // Cave background color
      scale: {
        mode: Phaser.Scale.FIT,
        autoCenter: Phaser.Scale.CENTER_BOTH,
        width: 800,
        height: 500
      },
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { y: 800 },
          debug: SHOW_DEBUG_BOUNDARIES
        }
      },
      scene: {
        preload: preload,
        create: create,
        update: update
      }
    };

    // Initialize the game
    gameInstanceRef.current = new Phaser.Game(config);
    
    // Game variables
    let player;
    let cursors;
    let platforms;
    let floor;
    let yetiGroup;
    let yetiSprite;
    let yetiParts = [];
    let discoballImage;
    let discoBall;
    let discoLights = [];
    let discoColors = [0xff0000, 0x00ff00, 0x0000ff, 0xff00ff, 0xffff00];
    let lightTimer = 0;
    
    // Game settings
    const settings = {
      floorY: 470,
      player: {
        scale: 0.2,
        width: 150,
        height: 350,
        offsetX: 0,
        offsetY: 0
      },
      yeti: {
        scale: 0.25,
        boxes: [
          { width: 150, height: 60, offsetX: -140, offsetY: -40 },
          { width: 80, height: 250, offsetX: 0, offsetY: -90 },
          { width: 90, height: 100, offsetX: 50, offsetY: -40 }
        ]
      },
      platforms: {
        scale: { x: 0.15, y: 0.2 },
        width: 160,
        height: 50,
        offsetX: 0,
        offsetY: 0
      }
    };
    
    function preload() { 
      this.load.image('cave', '/games/yeti-escape/cave.png');
      this.load.image('yeti', '/games/yeti-escape/yeti.png');
      this.load.image('discoball', '/games/yeti-escape/discoball.png');
      this.load.image('player', '/games/yeti-escape/player.png');
      this.load.image('platform', '/games/yeti-escape/platform.png');
    }

    function create() {
      // Add background
      this.add.image(400, 250, 'cave').setScale(0.9);
      
      // Create a static physics group for platforms
      platforms = this.physics.add.staticGroup();
      
      // Create a static physics sprite for the floor
      floor = this.physics.add.staticSprite(400, settings.floorY, null);
      floor.body.setSize(800, 20);
      floor.setVisible(false);
      
      // Add ground visual
      const groundVisual = this.add.image(400, 480, 'platform')
        .setScale(4, 0.25);
      
      // Add platform images with physics
      const platformPositions = [
        { x: 600, y: 400 },
        { x: 200, y: 400 },
        { x: 700, y: 280 },
        { x: 100, y: 250 }
      ];
      
      // Create platforms
      const platformSprites = platformPositions.map(pos => {
        const platform = this.add.image(pos.x, pos.y, 'platform')
          .setScale(settings.platforms.scale.x, settings.platforms.scale.y);
        platforms.add(platform);
        return platform;
      });
      
      // Adjust platform physics bodies
      platformSprites.forEach(platform => {
        platform.body.setSize(
          settings.platforms.width, 
          settings.platforms.height
        );
        platform.body.setOffset(
          settings.platforms.offsetX, 
          settings.platforms.offsetY
        );
      });
      
      // Add player with physics
      player = this.physics.add.sprite(100, 400, 'player')
        .setScale(settings.player.scale);
      
      player.body.setSize(
        settings.player.width, 
        settings.player.height
      );
      player.body.setOffset(
        settings.player.offsetX, 
        settings.player.offsetY
      );
      
      player.body.setBounce(0.1);
      player.body.setCollideWorldBounds(true);
      
      // Store player in ref for external access
      playerRef.current = player;
      
      // Create the yeti base sprite
      const yetiX = 400;
      const yetiY = 350;
      yetiSprite = this.add.image(yetiX, yetiY, 'yeti')
        .setScale(settings.yeti.scale);
      
      // Add yeti with physics
      yetiGroup = this.physics.add.staticGroup();
      
      // Create the yeti hitboxes
      settings.yeti.boxes.forEach((box, index) => {
        const partX = yetiX + box.offsetX;
        const partY = yetiY + box.offsetY;
        
        const yetiPart = this.physics.add.staticSprite(partX, partY, null)
          .setVisible(false);
        
        yetiPart.body.setSize(box.width, box.height);
        yetiGroup.add(yetiPart);
        yetiParts.push(yetiPart);
      });
      
      // Refresh all physics bodies
      platforms.refresh();
      yetiGroup.refresh();
      
      // Add disco ball above Yeti
      discoballImage = this.add.image(350, 90, 'discoball').setScale(0.25);
      discoBall = this.add.circle(350, 90, discoballImage.displayWidth / 2, 0xdcdde1).setAlpha(0);
      
      // Create disco lights
      for (let i = 0; i < 5; i++) {
        const light = this.add.circle(
          400 + Math.cos(i) * 150,
          300 + Math.sin(i) * 100,
          30,
          discoColors[i % discoColors.length],
          0.6
        );
        discoLights.push(light);
      }
      
      // Colliders
      this.physics.add.collider(player, platforms);
      this.physics.add.collider(player, floor);
      this.physics.add.collider(player, yetiGroup, function() {
        if (player.body.blocked.right || player.body.blocked.left) {
          player.body.setVelocityY(-200);
        }
      });
      
      // Input controls
      cursors = this.input.keyboard.createCursorKeys();
      cursors.one = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ONE);
      
      // Disco effect timer
      this.time.addEvent({
        delay: 200,
        callback: updateDiscoLights,
        callbackScope: this,
        loop: true
      });
    }

    function update() {
      // Player movement - keyboard controls
      // Only use keyboard controls if touch controls aren't active
      if (!player.isControlledByTouch) {
        if (cursors.left.isDown) {
          player.body.setVelocityX(-160);
          player.flipX = true;
        } else if (cursors.right.isDown) {
          player.body.setVelocityX(160);
          player.flipX = false;
        } else {
          player.body.setVelocityX(0);
        }
      }
      
      // Jump control
      const canJump = player.body.blocked.down || player.body.touching.down;
      if ((cursors.up.isDown || cursors.one?.isDown) && canJump) {
        player.body.setVelocityY(-470);
      }
      
      // Rotate the disco ball image
      discoballImage.angle += 1;
      
      // Pulse effect for the disco ball
      const pulseFactor = 0.1 * Math.sin(lightTimer * 3) + 1;
      discoBall.setScale(pulseFactor);
      
      // Reset the touch control flag at the end of each update
      player.isControlledByTouch = false;
    }
    
    function updateDiscoLights() {
      discoLights.forEach((light, index) => {
        light.fillColor = discoColors[Math.floor(Math.random() * discoColors.length)];
        
        const newX = 400 + Math.cos(lightTimer + index) * 200;
        const newY = 320 + Math.sin(lightTimer + index * 0.7) * 150;
        
        light.x = newX;
        light.y = newY;
        light.radius = 20 + Math.random() * 20;
      });
      
      lightTimer += 0.1;
    }
  };

  // Track which direction buttons are being held down
  const moveStateRef = useRef({
    left: false,
    right: false
  });
  
  // Direct touch control handlers that manipulate the player sprite directly
  const handleMove = (direction) => {
    if (!playerRef.current) return;
    
    // Set the movement state for this direction
    moveStateRef.current[direction] = true;
    
    // Apply movement immediately
    updatePlayerMovement();
  };
  
  const handleStopMove = (direction) => {
    if (!playerRef.current) return;
    
    // Clear the movement state for this direction
    moveStateRef.current[direction] = false;
    
    // Update movement based on any remaining active directions
    updatePlayerMovement();
  };
  
  // Helper to apply movement based on current state
  const updatePlayerMovement = () => {
    if (!playerRef.current) return;
    
    playerRef.current.isControlledByTouch = true;
    
    // Check which direction(s) are active
    if (moveStateRef.current.left && !moveStateRef.current.right) {
      playerRef.current.body.setVelocityX(-160);
      playerRef.current.flipX = true;
    } else if (moveStateRef.current.right && !moveStateRef.current.left) {
      playerRef.current.body.setVelocityX(160);
      playerRef.current.flipX = false;
    } else {
      // No direction or both directions (cancel out)
      playerRef.current.body.setVelocityX(0);
    }
  };
  
  const handleJump = () => {
    if (!playerRef.current) return;
    
    const canJump = playerRef.current.body.blocked.down || playerRef.current.body.touching.down;
    if (canJump) {
      playerRef.current.body.setVelocityY(-470);
    }
  };

  // Prevent default touch behavior
  const preventDefaultTouch = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    return false;
  };

  return (
    <div>
      <Hero
        title="Yeti Escape"
        description="Escape the Yeti's Wrath!"
        compact
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Game container */}
        <div className="flex justify-center">
          <div 
            ref={gameContainerRef} 
            className="w-full max-w-[800px] h-[500px] bg-gray-800 rounded-lg shadow-lg overflow-hidden relative"
            // Prevent scrolling when touching inside game area
            onTouchMove={preventDefaultTouch}
          >
            {/* Desktop controls hint */}
            <div className="absolute bottom-2 left-2 text-white bg-black bg-opacity-50 p-1 text-sm rounded hidden md:block">
              Use ← → to move and ↑ or 1 to jump
            </div>

            {/* Mobile touch controls - IMPROVED VERSION */}
            <div className="absolute bottom-4 left-4 right-4 md:hidden flex justify-between items-center z-50 select-none">
              {/* Left/Right controls */}
              <div className="flex gap-2">
                <button
                  onPointerDown={(e) => { preventDefaultTouch(e); handleMove('left'); }}
                  onPointerUp={(e) => { preventDefaultTouch(e); handleStopMove('left'); }}
                  onPointerOut={(e) => { preventDefaultTouch(e); handleStopMove('left'); }}
                  onPointerCancel={(e) => { preventDefaultTouch(e); handleStopMove('left'); }}
                  onPointerLeave={(e) => { preventDefaultTouch(e); handleStopMove('left'); }}
                  onTouchMove={preventDefaultTouch}
                  className="w-16 h-16 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white active:bg-opacity-75 touch-none select-none"
                  type="button"
                  style={{ touchAction: 'none' }}
                >
                  <ArrowLeft className="w-8 h-8" />
                </button>
                <button
                  onPointerDown={(e) => { preventDefaultTouch(e); handleMove('right'); }}
                  onPointerUp={(e) => { preventDefaultTouch(e); handleStopMove('right'); }}
                  onPointerOut={(e) => { preventDefaultTouch(e); handleStopMove('right'); }}
                  onPointerCancel={(e) => { preventDefaultTouch(e); handleStopMove('right'); }}
                  onPointerLeave={(e) => { preventDefaultTouch(e); handleStopMove('right'); }}
                  onTouchMove={preventDefaultTouch}
                  className="w-16 h-16 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white active:bg-opacity-75 touch-none select-none"
                  type="button"
                  style={{ touchAction: 'none' }}
                >
                  <ArrowRight className="w-8 h-8" />
                </button>
              </div>
              
              {/* Jump control */}
              <button
                onPointerDown={(e) => { preventDefaultTouch(e); handleJump(); }}
                onTouchMove={preventDefaultTouch}
                className="w-16 h-16 bg-black bg-opacity-50 rounded-full flex items-center justify-center text-white active:bg-opacity-75 touch-none select-none"
                type="button"
                style={{ touchAction: 'none' }}
              >
                <ArrowUp className="w-8 h-8" />
              </button>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-8">
          <Gamepad className="w-16 h-16 text-brand mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-content-primary mb-4">
            You have survived the Disco Yeti!
          </h2>
          <p className="text-content-secondary max-w-2xl mx-auto">
            This "anti-game" is a tribute to the broken Yeti animatronic at Disney's Animal Kingdom.
          </p>
        </div>
      </div>
    </div>
  );
};

export default YetiEscapePage;